import React, { useState, useRef, useEffect } from "react";
import { Button } from "../../common";
import { Alert } from "flowbite-react";
import { validateCustomerForm } from "../../../modules/customers/customers";
import { CustomerT } from "../../../types";
import { formatNumber, validatePhoneNumber } from "../../../utils";
import { InputComponent } from "../../core/Forms";
import { clearFieldError, handleErrors } from "../../../utils/handleErrors";
import LocationSelector from "../../common/Address";
import { ICustomer } from "../../../@types";
import PhoneInputComponent from "../../common/PhoneInputComponent";
import "react-phone-input-2/lib/style.css";
import throttle from "lodash/throttle";
import { CloseIcon } from "../../../assets/icons";
import CustomerLocationSelector from "../../common/Address/CustomerAddress";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../../../utils/trackEvent";
import { showToast } from "../../../utils/toastUtils";

interface CreateFormProps {
  show: boolean;
  onSubmit: any;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClose?: () => void;
  buttontitle: string;
  isLoading: boolean;
  customer?: CustomerT;
  errorMessage?: string;
  backendErrorMessage?: string;
  setIsLoadingPrompt?: any;
  setFormData: React.Dispatch<React.SetStateAction<ICustomer>>;
  handleClose?: () => void;
  title?: string;
  editUser?: boolean;
}
interface ValidationErrors {
  firstName: string;
  lastName: string;
  phone: string;
}

const CreateForm: React.FC<CreateFormProps> = (props) => {
  const { t } = useTranslation("customer");
  const {
    customer,
    onSubmit,
    isLoading,
    backendErrorMessage,
    setIsLoadingPrompt,
    setFormData,
    onClose,
    editUser,
  } = props;
  const [, setErrorMessage] = React.useState("");
  const [, setIsOpen] = React.useState(props.show);
  const [value, setValue] = useState("");
  const [backendErrorMessageHandler, setBackendErrorMessage] = React.useState(
    backendErrorMessage || "",
  );

  const [firstName, setFirstName] = React.useState(customer?.firstName);
  const [lastName, setlastName] = React.useState(customer?.lastName);
  const [email, setEmail] = React.useState(customer?.email);
  const [phone, setPhone] = React.useState(customer?.phone);
  const [address, setAddress] = React.useState(customer?.address);
  const [state, setState] = React.useState(customer?.state || "");
  const [city, setCity] = React.useState(customer?.city || "");

  const [businessName, setBusinessName] = React.useState(
    customer?.businessName,
  );
  const errorFieldRef = useRef<HTMLInputElement | null>(null);
  const [placeholder] = useState("+234 900 000 0000");
  const initialPhoneNumber = "+234";
  const [firstErrorField, setFirstErrorField] = useState<string | null>(null);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const inputRefs: Record<string, React.RefObject<HTMLInputElement>> = {
    firstName: useRef(null),
    lastName: useRef(null),
    phone: useRef(null),
  };

  const errorRefs: Record<string, React.RefObject<HTMLDivElement>> = {
    firstName: useRef(null),
    lastName: useRef(null),
    phone: useRef(null),
  };
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });

  const [validationResults, setValidationResults] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });

  function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  useEffect(() => {
    setBackendErrorMessage(backendErrorMessage || "");
  }, [backendErrorMessage]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    switch (name) {
      case "firstName":
        setFirstName(capitalizeFirstLetter(value));
        break;
      case "lastName":
        setlastName(capitalizeFirstLetter(value));
        break;
      case "email":
        setEmail(value.toLowerCase());
        // Email validation: check if the email contains "@"
        if (!value.includes("@")) {
          setValidationResults((prevResults) => ({
            ...prevResults,
            email: 'Invalid email address. Please include "@" symbol.',
          }));
        } else {
          clearFieldError(name, validationResults, setValidationResults);
        }
        break;
      case "phone":
        setPhone(formatNumber(value));
        break;
      case "address":
        setAddress(value);
        break;
      case "city":
        setCity(value);
        break;
      case "state":
        setState(value);
        break;
      case "businessName":
        setBusinessName(capitalizeFirstLetter(value));
        break;
      default:
        break;
    }
    clearFieldError(name, validationResults, setValidationResults);
  };

  const throttledSubmit = useRef(
    throttle((newCustomer: any) => {
      onSubmit(newCustomer);
    }, 2000),
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const { hasErrors, errors: validationErrors } = validateCustomerForm({
      firstName,
      lastName,
      phone,
    });

    if (hasErrors) {
      setErrors(validationErrors); // Set errors to state
      return; // Prevent form submission
    }

    let phoneNumberToSend = phone || "";

    const newCustomer = {
      firstName,
      lastName,
      phone: phoneNumberToSend,
      address,
      email,
      businessName,
      state,
      city,
    };

    try {
      await onSubmit(newCustomer);
      if (onClose) {
        onClose();
      }
      if (!customer) {
        trackEvent("customer_create_success");
        console.log("success ...");
      }
    } catch (error: any) {
      if (!customer) {
        trackEvent("customer_create_error");
      }
      let errorMessage = "An unknown error occurred";

      if (error.response && error.response.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error("Submission error:", errorMessage);
      showToast(errorMessage, "error")
    }
  };

  const handlePhoneNumberChange = (value: string) => {
    const normalizedValue = value.replace(/\D/g, "");

    const isValidPhoneNumber = validatePhoneNumber(normalizedValue);

    if (isValidPhoneNumber) {
      // Format to include country code if necessary
      const formattedPhoneNumber = normalizedValue.startsWith("+")
        ? normalizedValue
        : `+${normalizedValue}`;

      setPhone(formattedPhoneNumber);
      setValidationResults({ ...validationResults, phone: "" });
    } else {
      setValidationResults({
        ...validationResults,
        phone: "Invalid phone format",
      });
    }
  };

  React.useEffect(() => {
    setIsOpen(props.show);
  }, []);

  React.useEffect(() => {
    setPhone(customer?.phone || "");
  }, [customer]);

  useEffect(() => {
    let intervalId: any;
    if (backendErrorMessageHandler) {
      intervalId = setTimeout(() => {
        setBackendErrorMessage("");
      }, 7000);
    }

    return () => {
      clearTimeout(intervalId);
    };
  }, [backendErrorMessageHandler]);

  const handleLocationChange = (
    selectedState: string,
    cities: string | any[],
  ) => {
    setState(selectedState);
    setCity(cities.length > 0 ? cities[0] : "");
  };

  const handlePhoneNumberKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.currentTarget.value.length >= 17) {
      event.preventDefault();
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowAddressFields(e.target.checked);
  };
  const [phoneNumber, setPhoneNumber] = useState("");
  const handleChange = (value: React.SetStateAction<string>) => {
    setPhoneNumber(value);
  };

  return (
    <>
      <div className="text-center 360:text-center ">
        {" "}
        <form className="" action="" onSubmit={handleSubmit}>
          {backendErrorMessageHandler && (
            <Alert className="text-center" color="failure">
              {backendErrorMessageHandler}
            </Alert>
          )}
          {/* mobile */}
          <div className=" lg:hidden md:hidden w-full h-full min-h-screen md:min-h-max   md:pb-6">
            <div
              id="input-container"
              className="xl:px-[5px] xl:w-[434px] w-full md:w-[420px]"
              ref={errorFieldRef}
            >
              <label
                htmlFor="First Name"
                className="flex gap-2  font-medium text-[13px] text-black"
                style={{ whiteSpace: "nowrap" }}
              >
                First Name
                <span className="text-red-500">*</span>
              </label>
              <InputComponent
                placeholder="First Name"
                name="firstName"
                type="text"
                value={firstName}
                handleChange={handleInputChange}
                error={errors.firstName}
                className="rounded-[5px] xl:w-[324px] w-full md:w-[324px]"
                ref={inputRefs.firstName}
              />
            </div>
            <div className=" mt-[16px] xl:w-[324px] w-full md:w-[324px]">
              <label
                htmlFor="Last Name"
                className="flex gap-2  font-medium text-[13px] text-black"
                style={{ whiteSpace: "nowrap" }}
              >
                Last Name
                <span className="text-red-500">*</span>
              </label>
              <InputComponent
                placeholder="Last Name"
                name="lastName"
                type="text"
                value={lastName}
                handleChange={handleInputChange}
                error={errors.lastName}
                className="rounded-[5px] xl:w-[324px] w-full md:w-[324px]"
                ref={inputRefs.lastName}
              />
            </div>
            <div className="mt-[16px] flex flex-col justify-start items-start  lg:mt-[37px] xl:w-[434px] w-full md:w-[420px]">
              <label
                htmlFor="Phone Number"
                className="flex gap-2 font-medium text-[13px] text-black"
                style={{ whiteSpace: "nowrap" }}
              >
                {t("customer.phone")}
                <span className="text-red-500">*</span>
              </label>

              <PhoneInputComponent
                onPhoneNumberChange={handlePhoneNumberChange}
                initialPhoneNumber={phone}
                containerClass="customer"
                inputClass="customer"
                isUser={editUser}
              />
              <div className="">
                {errors.phone && errors.phone.length > 0 && (
                  <p className="text-red-500 text-sm">{errors.phone}</p>
                )}
              </div>
            </div>
            <div className=" mt-[16px] lg:mt-[37px]">
              <label
                htmlFor="Email"
                className="flex text-[13px]  font-medium text-black justify-between"
              >
                Email
              </label>
              <InputComponent
                name="email"
                placeholder="Email Address"
                type="text"
                value={email}
                handleChange={handleInputChange}
                className="rounded-[5px] xl:w-[324px] w-full md:w-[324px]"
              />
            </div>
            <div className="flex items-center mt-[2em] biglg:ml-[1em] xl:ml-[0.3em]">
              <input
                type="checkbox"
                id="toggleAddressFields"
                checked={showAddressFields}
                onChange={handleCheckboxChange}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="link-checkbox"
                className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
              >
                Click here to fill in more details.
              </label>
            </div>
            {showAddressFields && (
              <div>
                <div className="mt-[16px]  lg:mt-[37px]">
                  <label
                    htmlFor="BusinessName"
                    className="block text-sm font-medium  text-left "
                  >
                    {t("customer.businessName")}
                  </label>
                  <InputComponent
                    name="businessName"
                    placeholder="Business Name"
                    type="text"
                    value={businessName}
                    handleChange={handleInputChange}
                    className="rounded-[5px] xl:w-[434px] w-full md:w-[420px] lg:ml-[-0.6em]"
                  />
                </div>
                <div className=" mt-[16px]  lg:mt-[37px]">
                  <label
                    htmlFor="Address"
                    className="block text-[13px] font-medium  text-left biglg:ml-[1em] xl:ml-[0.3em]"
                  >
                    {t("customer.address")}
                  </label>
                  <InputComponent
                    placeholder="Billing Address"
                    name="address"
                    type="text"
                    value={address}
                    handleChange={handleInputChange}
                    className="rounded-[5px] xl:w-[324px] w-full md:w-[324px]"
                  />
                </div>
                <div className="w-full mt-[1em] md:w-[420px] xl:w-[434px] lg:mt-[1em] xl:mt-[2.5em]">
                  <LocationSelector
                    onLocationChange={handleLocationChange}
                    onError={(error: any) => {}}
                    selectedState={state}
                    selectedCity={city}
                  />
                </div>
              </div>
            )}
            <div className="mt-[32px] flex gap-6 w-full">
              <Button
                outline={true}
                variant="secondary"
                disabled={false}
                text={"Cancel"}
                size="full"
                onClick={onClose}
              />
              <Button
                isLoading={isLoading}
                text={props.buttontitle}
                size="full"
                variant="secondary"
                type="submit"
              />
            </div>
          </div>
        </form>
      </div>

      {/* Desktop */}
      <div className="text-center 360:text-center ">
        {" "}
        <form className="" action="" onSubmit={handleSubmit}>
          {backendErrorMessageHandler && (
            <Alert className="text-center" color="failure">
              {backendErrorMessageHandler}
            </Alert>
          )}
          <div className="hidden md:block lg:block max-w-[772px] px-2 pb-8">
            <div className="flex items-center justify-between">
              <h3 className="text-[24px] font-medium">{props.title}</h3>

              <button
                aria-label="Close"
                className="flex items-center justify-center  h-11 w-11 pr-[1em]"
                type="button"
                onClick={onClose}
              >
                <CloseIcon color="#212121" />
              </button>
            </div>
            {/* line 1 */}
            <div className="flex justify-between gap-4 mt-[2em] text-[16px]">
              <div className="col-span-1">
                <label className="text-sm  font-medium flex gap-2 ">
                  First name
                  <span className="text-red-500 ">*</span>
                </label>
                <InputComponent
                  name="firstName"
                  type="text"
                  className="mt-1 block w-[350px] border border-gray-300 rounded-md shadow-sm sm:text-sm"
                  placeholder="First name"
                  value={firstName}
                  handleChange={handleInputChange}
                  error={errors.firstName}
                  ref={inputRefs.firstName}
                />
              </div>

              <div className="col-span-1">
                <label className=" text-sm  font-medium  flex gap-2 text-[16px]">
                  Last name
                  <span className="text-red-500 text-[16px]">*</span>
                </label>
                <InputComponent
                  name="lastName"
                  type="text"
                  className="mt-1 block w-[350px] border border-gray-300 rounded-md shadow-sm sm:text-sm"
                  placeholder="Last name"
                  value={lastName}
                  handleChange={handleInputChange}
                  error={errors.lastName}
                  ref={inputRefs.lastName}
                />
              </div>
            </div>
            {/* Line 2 */}

            <div className="grid grid-cols-2 gap-4 text-[16px] mt-[2em]">
              <div className="col-span-1 flex flex-col justify-start items-start">
                <label className="text-sm  font-medium flex gap-2 md:mb-[4px]">
                  {" "}
                  {t("customer.phone")}
                  <span className="text-red-500 ">*</span>
                </label>
                <PhoneInputComponent
                  onPhoneNumberChange={handlePhoneNumberChange}
                  initialPhoneNumber={phone}
                  containerClass="customer"
                  inputClass="customer"
                  isUser={editUser}
                />
                <div className="">
                  {errors.phone && errors.phone.length > 0 && (
                    <p className="text-red-500 text-sm">{errors.phone}</p>
                  )}
                </div>
              </div>
              <div className="col-span-1">
                <label className=" text-sm  font-medium flex justify-between ">
                  Email
                  <span className="text-red-500 "></span>
                </label>
                <InputComponent
                  name="email"
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm sm:text-sm"
                  placeholder="Enter email"
                  value={email}
                  handleChange={handleInputChange}
                />
              </div>
            </div>

            <div className="flex items-center mt-[2em] biglg:ml-[1em] xl:ml-[0.3em]">
              <input
                type="checkbox"
                id="toggleAddressFields"
                checked={showAddressFields}
                onChange={handleCheckboxChange}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="link-checkbox"
                className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
              >
                Click here to fill in more details.
              </label>
            </div>

            {/* Line 3 */}
            {showAddressFields && (
              <div>
                <div className="flex items-center justify-between text-[16px] mt-[2em]">
                  <div className="col-span-1">
                    <label className=" text-sm  font-medium flex justify-between ">
                      {t("customer.businessName")}
                      <span className="text-red-500 "></span>
                    </label>
                    <InputComponent
                      name="businessName"
                      type="text"
                      className="mt-1 block w-[350px] border border-gray-300 rounded-md shadow-sm sm:text-sm"
                      placeholder="Enter business name"
                      value={businessName}
                      handleChange={handleInputChange}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className=" text-sm  font-medium flex justify-between  ">
                      {t("customer.address")}
                      <span className="text-red-500 "></span>
                    </label>
                    <InputComponent
                      name="address"
                      type="text"
                      className="mt-1 block w-[350px] border border-gray-300 rounded-md shadow-sm sm:text-sm"
                      placeholder="Add address"
                      value={address}
                      handleChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Line 4 */}
                <div className=" text-[16px] mt-[2em]">
                  <div className="col-span-1">
                    <CustomerLocationSelector
                      onLocationChange={handleLocationChange}
                      onError={(error: any) => {}}
                      selectedState={state}
                      selectedCity={city}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="mt-[2em] flex gap-6 w-full justify-end items-end">
              <Button
                outline={true}
                variant="secondary"
                disabled={false}
                text={"Cancel"}
                size="small"
                onClick={onClose}
              />
              <Button
                isLoading={isLoading}
                text={props.buttontitle}
                size="small"
                variant="secondary"
                type="submit"
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateForm;
